import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock, MessageCircle, DoorOpen, ZoomIn, X } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { heroText } from "@/lib/pageHero";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { PageContentBlocks } from "@/components/site/PageContentBlocks";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";
import { SectionShell } from "@/components/site/edit/SectionShell";

function waLink(number, title) {
  const n = (number || "").replace(/[^0-9]/g, "");
  return `https://wa.me/${n}?text=${encodeURIComponent(`Hi! I'd like to join the "${title}" class. Please share the details.`)}`;
}

export default function ClassesPage() {
  const location = useLocation();
  const { t, tl, lang } = useLang();
  const { data: classes = [] } = useQuery({
    queryKey: ["weekly-classes"],
    queryFn: async () => (await api.get("/weekly-classes")).data,
  });
  const { data: settings = {} } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const shul = settings.synagogue;
  const shulTitle = shul && (lang !== "en" && shul[`title_${lang}`] ? shul[`title_${lang}`] : shul.title);
  const shulText = shul && (lang !== "en" && shul[`text_${lang}`] ? shul[`text_${lang}`] : shul.text);
  const weekdayPrayers = (shul && shul.prayers_weekday) || [];
  const shabbatPrayers = (shul && shul.prayers_shabbat) || [];
  const hasPrayers = weekdayPrayers.length > 0 || shabbatPrayers.length > 0;
  const holidayFlyer = shul && shul.holiday_flyer ? resolveImg(shul.holiday_flyer) : "";
  const holidayLabel = { en: "Upcoming Holiday Prayer Times", cs: "Časy modliteb na nadcházející svátky", he: "זמני תפילה לחגים הקרובים", ru: "Расписание молитв на праздники" }[lang] || "Upcoming Holiday Prayer Times";
  const headerImg = settings?.page_headers?.classes;
  const ht = heroText(settings, "classes", lang, t, "classespage");
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="relative bg-[#F9F8F6] min-h-screen overflow-x-hidden" data-testid="classes-page">
      <div className="grain" />
      <Navbar />

      <PageHero image={headerImg} eyebrow={ht("eyebrow")} title={ht("title")} description={ht("desc")} icon={DoorOpen} testid="classes-hero" />

      <PageContentBlocks page="classes" position="top" />

      <SectionShell id="classes-sec-1" label="Section 1" noReorder>
      <section className="pt-12 sm:pt-14 pb-16 sm:pb-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 space-y-8">
          {classes.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.05}>
              <div id={`class-${c.id}`} data-testid={`class-detail-${c.id}`} className="grid md:grid-cols-2 gap-0 rounded-[2rem] overflow-hidden bg-white border border-[#0F172A]/5 shadow-[0_12px_40px_rgba(15,23,42,0.06)] scroll-mt-28">
                <div className={`relative h-64 md:h-auto min-h-[280px] overflow-hidden ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  {c.image_url ? (
                    <img src={resolveImg(c.image_url)} alt={tl(c, "title")} className="absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 bg-[#0F172A]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/40 to-transparent md:hidden" />
                </div>
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  {c.audience && <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#BE923C]">{tl(c, "audience")}</span>}
                  <h2 className="mt-2 font-serif text-3xl sm:text-4xl leading-tight">{tl(c, "title")}</h2>
                  <p className="mt-3 inline-flex items-center gap-2 text-[#475569] font-medium"><Clock size={16} className="text-[#BE923C]" /> {tl(c, "time")}</p>
                  {tl(c, "description") && <p className="mt-5 text-[#475569] leading-relaxed">{tl(c, "description")}</p>}
                  {c.wa_button_enabled !== false && (
                    <a
                      href={waLink(c.whatsapp_number, c.title)}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`class-whatsapp-${c.id}`}
                      className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-[#0F172A] hover:bg-[#1e293b] text-white font-semibold px-7 py-3.5 transition-colors"
                    >
                      <MessageCircle size={18} /> {tl(c, "wa_button_text") ? tl(c, "wa_button_text") : <EditableText k="classespage.join_wa" as="span" />}
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
          {classes.length === 0 && <p className="text-[#475569]"><EditableText k="classespage.none" as="span" /></p>}
        </div>
      </section>
      </SectionShell>

      {shul && (
        <SectionShell id="classes-sec-2" label="Section 2" noReorder>
        <section className="relative z-10 py-16 sm:py-20 bg-[#0F172A] text-white" data-testid="classes-shul">
          <div className="mx-auto max-w-6xl px-5 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
            <Reveal>
              {shul.image_url && (
                <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
                  <img src={resolveImg(shul.image_url)} alt={shulTitle} className="w-full h-72 sm:h-80 object-cover" />
                </div>
              )}
            </Reveal>
            <Reveal delay={0.1}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><DoorOpen size={14} /> <EditableText k="weekly.ourshul" as="span" /></span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">{shulTitle}</h2>
              <p className="mt-4 text-white/75 leading-relaxed">{shulText}</p>
              <Link to="/#contact" className="mt-6 inline-block rounded-full bg-white text-[#0F172A] font-semibold px-7 py-3.5 hover:bg-white/90 transition-colors"><EditableText k="classespage.visit" as="span" /></Link>
            </Reveal>
          </div>

          {hasPrayers && (
            <div id="shul-prayer-times" className="mx-auto max-w-6xl px-5 sm:px-8 mt-12" data-testid="shul-prayer-times">
              <Reveal>
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-white flex items-center gap-2.5"><Clock size={22} className="text-[#BE923C]" /> <EditableText k="shul.prayer_times" as="span" /></h3>
              </Reveal>
              <div className="mt-6 grid md:grid-cols-2 gap-5">
                {[{ list: weekdayPrayers, title: t("shul.weekday") }, { list: shabbatPrayers, title: t("shul.shabbat") }].filter((g) => g.list.length > 0).map((grp, gi) => (
                  <Reveal key={gi} delay={gi * 0.08}>
                    <div className="rounded-[1.75rem] bg-white/[0.06] border border-white/10 backdrop-blur-sm p-6 sm:p-7">
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#D8B45E]">{grp.title}</p>
                      <ul className="mt-4 divide-y divide-white/10">
                        {grp.list.map((p, i) => (
                          <li key={i} className="flex items-center justify-between gap-4 py-2.5">
                            <span className="text-white/90">{(lang !== "en" && p[`label_${lang}`]) || p.label}</span>
                            <span className="font-semibold text-white shrink-0">{(lang !== "en" && p[`time_${lang}`]) || p.time}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {holidayFlyer && (
            <div id="shul-holiday-flyer" className="mx-auto max-w-6xl px-5 sm:px-8 mt-12" data-testid="shul-holiday-flyer">
              <Reveal>
                <h3 className="font-serif text-2xl sm:text-3xl tracking-tight text-white flex items-center gap-2.5"><Clock size={22} className="text-[#BE923C]" /> {holidayLabel}</h3>
              </Reveal>
              <Reveal delay={0.08}>
                <button type="button" onClick={() => setZoom(true)} data-testid="holiday-flyer-zoom"
                  className="group/flyer relative mt-6 block w-full max-w-2xl mx-auto cursor-zoom-in rounded-[1.75rem] overflow-hidden border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
                  <img src={holidayFlyer} alt={holidayLabel} data-testid="holiday-flyer" className="w-full object-cover" />
                  <span className="absolute bottom-4 end-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-3 py-1.5 text-xs font-semibold text-white opacity-0 group-hover/flyer:opacity-100 transition-opacity"><ZoomIn size={15} /></span>
                </button>
              </Reveal>
            </div>
          )}
        </section>
        </SectionShell>
      )}

      {zoom && holidayFlyer && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" data-testid="holiday-flyer-lightbox" onClick={() => setZoom(false)}>
          <button type="button" data-testid="holiday-flyer-lightbox-close" onClick={() => setZoom(false)} className="absolute top-4 end-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"><X size={22} /></button>
          <img src={holidayFlyer} alt={holidayLabel} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" />
        </div>
      )}

      <PageContentBlocks page="classes" position="bottom" />

      <Footer />
    </div>
  );
}
